/**
 * Dutch presentation metadata for medication occurrence/intake statuses
 * (spec §9.5 status union). Status is never conveyed by color alone:
 * every chip pairs a Dutch label with the StatusChip icon.
 */
import type { OccurrenceStatus } from '@/types'
import type { StatusChipStatus } from '@/components/ui'

export type OccurrenceStatusMeta = {
  label: string
  chip: StatusChipStatus
}

const STATUS_META: Record<OccurrenceStatus, OccurrenceStatusMeta> = {
  planned: { label: 'Gepland', chip: 'neutral' },
  due: { label: 'Nu innemen', chip: 'info' },
  taken: { label: 'Ingenomen', chip: 'success' },
  proof_uploading: { label: 'Bewijs uploaden', chip: 'info' },
  proof_uploaded: { label: 'Bewijs geüpload', chip: 'success' },
  proof_viewed: { label: 'Bekeken door begeleider', chip: 'success' },
  missed: { label: 'Gemist', chip: 'warning' },
  cancelled: { label: 'Geannuleerd', chip: 'neutral' },
}

export function occurrenceStatusMeta(status: OccurrenceStatus): OccurrenceStatusMeta {
  return STATUS_META[status] ?? { label: status, chip: 'neutral' }
}

/** Statuses on which the client may still mark the occurrence as taken. */
export function canMarkTaken(status: OccurrenceStatus): boolean {
  return status === 'planned' || status === 'due' || status === 'missed'
}

/** Statuses where a proof video can be recorded/uploaded (intake exists, no proof yet). */
export function canAttachProof(status: OccurrenceStatus): boolean {
  return status === 'taken'
}

/** Proof is present and waiting for caregiver review. */
export function isProofPendingReview(status: OccurrenceStatus): boolean {
  return status === 'proof_uploaded'
}
